import React, { useState } from 'react';
import Card from '../../components/common/Card';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import EscalationMatrixManager from '../../components/features/escalation-matrix/EscalationMatrixManager';

const ManagerSettingsView = () => {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  
  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    contact: user?.contact || '',
    designation: user?.designation || '',
  });

  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const handleProfileChange = (e, field) => {
    setProfile(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handlePasswordChange = (e, field) => {
    setPasswords(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSaveProfile = async () => {
    setIsSavingProfile(true);
    try {
      const response = await api.put(`/api/v1/users/${user.id}`, {
        name: profile.name,
        email: profile.email,
        contact: profile.contact,
        designation: profile.designation
      });
      updateUser(response.data);
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Failed to update profile:', error);
      alert(error.response?.data?.detail || 'Failed to update profile.');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!passwords.new_password || !passwords.current_password) {
      return alert('New and Current passwords are required.');
    }
    if (passwords.new_password !== passwords.confirm_password) {
      return alert('New passwords do not match.');
    }

    setIsUpdatingPassword(true);
    try {
      await api.put(`/api/v1/users/${user.id}/password`, {
        current_password: passwords.current_password,
        new_password: passwords.new_password
      });
      alert('Password updated successfully!');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      console.error('Failed to update password:', error);
      alert(error.response?.data?.detail || 'Failed to update password. Make sure your current password is correct.');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const tabsStyle = {
    display: 'flex',
    gap: '8px',
    marginBottom: '24px',
    borderBottom: '1px solid var(--border)',
    paddingBottom: '0'
  };

  const tabStyle = (isActive) => ({
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '600',
    background: 'transparent',
    border: 'none',
    borderBottom: isActive ? '3px solid var(--primary)' : '3px solid transparent',
    color: isActive ? 'var(--primary)' : 'var(--text-secondary)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit'
  });

  const sectionStyle = {
    marginBottom: '32px'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '16px'
  };

  const renderProfileTab = () => (
    <div>
      <div style={sectionStyle}>
        <Card title="Personal Information" padding="24px">
          <div style={gridStyle}>
            <Input
              label="Full Name"
              value={profile.name}
              onChange={(e) => handleProfileChange(e, 'name')}
            />
            <Input
              label="Email"
              type="email"
              value={profile.email}
              onChange={(e) => handleProfileChange(e, 'email')}
            />
            <Input
              label="Phone"
              value={profile.contact}
              onChange={(e) => handleProfileChange(e, 'contact')}
            />
            <Input
              label="Designation"
              value={profile.designation || ''}
              disabled
              onChange={(e) => handleProfileChange(e, 'designation')}
            />
            <Input
              label="Role"
              value={user?.role || ''}
              disabled
            />
          </div>
          <div style={{ marginTop: '20px', display: 'flex', gap: '12px' }}>
            <Button variant="primary" onClick={handleSaveProfile} disabled={isSavingProfile}>
              {isSavingProfile ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button 
               variant="secondary" 
               onClick={() => setProfile({ name: user?.name, email: user?.email, contact: user?.contact })}
            >
              Cancel
            </Button>
          </div>
        </Card>
      </div>

      <div style={sectionStyle}>
        <Card title="Change Password" padding="24px">
          <div style={gridStyle}>
            <Input
              label="Current Password"
              type="password"
              canPeekPassword
              placeholder="Enter current password"
              value={passwords.current_password}
              onChange={(e) => handlePasswordChange(e, 'current_password')}
            />
            <Input
              label="New Password"
              type="password"
              canPeekPassword
              placeholder="Enter new password"
              value={passwords.new_password}
              onChange={(e) => handlePasswordChange(e, 'new_password')}
            />
            <Input
              label="Confirm Password"
              type="password"
              canPeekPassword
              placeholder="Confirm new password"
              value={passwords.confirm_password}
              onChange={(e) => handlePasswordChange(e, 'confirm_password')}
            />
          </div>
          <div style={{ marginTop: '20px' }}>
            <Button variant="primary" onClick={handleUpdatePassword} disabled={isUpdatingPassword}>
              {isUpdatingPassword ? 'Updating...' : 'Update Password'}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );

  return (
    <div>
      <div style={tabsStyle}>
        <button
          style={tabStyle(activeTab === 'profile')}
          onClick={() => setActiveTab('profile')}
        >
          Profile
        </button>
        <button
          style={tabStyle(activeTab === 'escalation')}
          onClick={() => setActiveTab('escalation')}
        >
          Escalation Matrix
        </button>
      </div>

      {activeTab === 'profile' && renderProfileTab()}
      {activeTab === 'escalation' && <EscalationMatrixManager />}
    </div>
  );
};

export default ManagerSettingsView;
